import type { AgentStep, QueryResponse, Visualization } from "@unbox/api";
import type { Message } from "@/pages/ask/components/MessageBubble";
import type { StreamingPhase } from "@/pages/ask/lib/stream-status";

export type SSEEvent =
  | { type: "conversation"; data: { conversation_id: string; message_id: string } }
  | { type: "delta"; data: { text: string } }
  | { type: "step_start"; data: { label: string } }
  | { type: "step_result"; data: AgentStep }
  | { type: "finding"; data: { index: number; text: string } }
  | { type: "phase"; data: StreamingPhase }
  | { type: "display"; data: { visualization: Visualization | null } }
  | { type: "result"; data: QueryResponse };

export type ReducerCtx = { msgId: string; question: string; sessionId: string };

/**
 * Pure: given the current messages and one SSE event, return the next messages.
 * Only mutates the in-flight message (id === ctx.msgId). The `error` event is
 * handled by the caller (it throws), so it is intentionally not modelled here.
 */
export function applySSEEvent(messages: Message[], event: SSEEvent, ctx: ReducerCtx): Message[] {
  return messages.map((m) => {
    if (m.id !== ctx.msgId) return m;
    switch (event.type) {
      case "conversation":
        return { ...m, messageId: event.data.message_id };
      case "delta":
        return { ...m, content: (m.content ?? "") + event.data.text };
      case "step_start":
        return { ...m, currentStepLabel: event.data.label, agentSteps: m.agentSteps ?? [] };
      case "step_result":
        return { ...m, currentStepLabel: undefined, agentSteps: [...(m.agentSteps ?? []), event.data] };
      case "finding":
        return { ...m, streamingFindings: [...(m.streamingFindings ?? []), event.data.text] };
      case "phase":
        return { ...m, streamingPhase: event.data };
      case "display":
        return { ...m, streamingDisplay: event.data.visualization };
      case "result":
        return {
          ...m,
          content: event.data.explanation ?? "",
          queryResponse: event.data,
          agentSteps: undefined,
          currentStepLabel: undefined,
          streamingFindings: undefined,
          streamingPhase: undefined,
          streamingDisplay: undefined,
          question: ctx.question,
          sessionId: ctx.sessionId,
        };
    }
  });
}
